import { message } from 'antd';
import { t } from 'i18next';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';
import IosModal from '../../components/Common/IosModal/IosModal';
import Search from '../../components/Common/Search/Search';
import UserBanner from '../../components/Common/UserBanner/UserBanner';
import Modal from '../../components/Modal/Modal';
import Title from '../../components/Title/Title';
import UserProfile from '../../components/UserProfile/UserProfile';

import { TRootState } from '../../store/store';
import { transport } from '../../utils/constants';
import { copyToClipboard, getToken, isRpcError, setAvas } from '../../utils/helper';
import './Team.scss';
import { Account_Info } from '../../generated/mlmbox/types/account';
import { TeamResponse } from '../../generated/mlmbox/client/network/network';
import { NetworkClient } from '../../generated/mlmbox/client/network/network.client';
import { Sort_Direction } from '../../generated/mlmbox/types/sort';
import Button from '../../components/Button/Button';
import { copyIcon } from '../../assets';

export function getIdsByBitMask(num: number, channelIds: number[]): string {
    if (!channelIds.includes(1) && channelIds.includes(2)) return 'Ducks Tap';
    if (!channelIds.includes(1) && channelIds.includes(3)) return 'TTM Casino';
    const ids: number[] = [];
    let position = 0;

    while (num !== 0) {
        if (num & 1) {
            if (position + 1 < 16) ids.push(position + 1); // Увеличиваем позицию на 1
        }
        num >>= 1;
        position++;
    }
    if (ids.length === 0) return t('not_areas');
    return `P${ids.slice(-1)}`;
}

function Statistic() {
    const statistics = useSelector((state: TRootState) => state.user.value.info.statistics);
    const refId = useSelector((state: TRootState) => state.user.value.info.account.refLinkId);
    const { t } = useTranslation();

    return (
        <div className="statistic">
            <div className="statistic__data">
                <div className="statistic__data--info">
                    <h2>{statistics.teamQuantity}</h2>
                    <p dangerouslySetInnerHTML={{ __html: t('team.teamQuantity') }}></p>
                </div>
                <div className="statistic__data--info">
                    <h2>{statistics.partnersQuantity}</h2>
                    <p dangerouslySetInnerHTML={{ __html: t('team.partnersQuantity') }}></p>
                </div>
                <div className="statistic__data--info">
                    <h2>{statistics.marketingTeamQuantity}</h2>
                    <p dangerouslySetInnerHTML={{ __html: t('team.matrixTeamQuantity') }}></p>
                </div>
                <div className="statistic__data--info">
                    <h2>{statistics.marketingPartnersQuantity}</h2>
                    <p dangerouslySetInnerHTML={{ __html: t('team.matrixPartnersQuantity') }}></p>
                </div>
            </div>
            <div className="statistic__link">
                <div
                    className="input"
                    onClick={async () => {
                        message.success(t('team.copy'));
                        await copyToClipboard(`https://t.me/TTM_partners_bot/TTM?startapp=r-${refId}`);
                    }}>
                    <p>{`https://t.me/TTM_partners_bot/TTM?startapp=r-${refId}`}</p>
                </div>
                <Button
                    width="48px"
                    icon={copyIcon}
                    height="48px"
                    onClick={async () => {
                        message.success(t('team.copy'));
                        await copyToClipboard(`https://t.me/TTM_partners_bot/TTM?startapp=r-${refId}`);
                    }}
                />

                <div className="line"></div>
            </div>
        </div>
    );
}

export interface IPartnerItem {
    profile: {
        firstName: string;
        lastName: string;
        tgId: number;
        ava?: {
            collectionId: number;
            entityId: number;
        };
    };
}

const Team: FC = () => {
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = useState<string>('');
    const [debouncedValue] = useDebounce(searchValue, 700);
    const [searching, setSearching] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [openSearch, setOpenSearch] = useState(false);
    const statistics = useSelector((state: TRootState) => state.user.value.info.statistics);
    const [openProfile, setOpenProfile] = useState<TeamResponse | null>(null);

    const [teamList, setTeamList] = useState<Account_Info[]>([]);

    const [_, setSearchList] = useState<Account_Info[]>([]);

    const accountId = useSelector((state: TRootState) => state.user.value.info.account.id);

    const teamClient = new NetworkClient(transport);

    useEffect(() => {
        if (!accountId) return;

        teamClient
            .teamInfo(
                {
                    filterChannels: [1],
                    filterTreeIdsBitMask: 0,
                    partnersSort: { direction: Sort_Direction.FORWARD, limit: 50 },
                    viewPartner: { uniqueField: { accountId, oneofKind: 'accountId' } },
                },
                { meta: { authorization: getToken() } }
            )
            .then(async ({ status, response }) => {
                if (status.code === 'OK') {
                    await setAvas(response.partners);
                    setTeamList(response.partners);
                }
                setSearching(false);
            });
    }, [accountId]);

    function loadPartners() {
        if (fetching) return;
        setFetching(true);
        if (teamList.length >= statistics.partnersQuantity) return;

        teamClient
            .teamPartnerList(
                {
                    stepReferralBranch: teamList.length,
                    request: {
                        partnersSort: { direction: Sort_Direction.FORWARD, limit: 50 },
                        viewPartner: { uniqueField: { accountId, oneofKind: 'accountId' } },
                        filterChannels: [1],
                        filterTreeIdsBitMask: 0,
                    },
                },
                { meta: { authorization: getToken() } }
            )
            .then(async ({ status, response }) => {
                if (status.code === 'OK') {
                    await setAvas(response.items);
                    setTeamList((prev) => [...prev, ...response.items]);
                    setFetching(false);
                }
                setSearching(false);
            });
    }

    function teamSearch() {
        teamClient
            .teamSearch({ filterChannels: [1], limit: 10, loginPrefix: searchValue }, { meta: { authorization: getToken() } })
            .then(({ status, response }) => {
                if (status.code === 'OK') {
                    setSearchList(response.items);
                    setSearching(false);
                }
            })
            .catch((err) => {
                if (isRpcError(err)) {
                    if (err.message === 'LOGIN_FORMAT_UNSUPPORTED') message.error('Не верный формат логина');

                    setSearching(false);
                    setSearchValue('');
                    setSearchList([]);
                }
            });
    }

    useEffect(() => {
        if (!searchValue.length) {
            setSearchList([]);
            return;
        }
        setSearching(true);
        teamSearch();
    }, [debouncedValue]);

    function getAccount(id: number) {
        const accClient = new NetworkClient(transport);

        accClient
            .teamInfo(
                {
                    filterChannels: [1],
                    filterTreeIdsBitMask: 0,
                    partnersSort: { direction: Sort_Direction.BACKWARD, limit: 10 },
                    viewPartner: { uniqueField: { accountId: id, oneofKind: 'accountId' } },
                },
                { meta: { authorization: getToken() } }
            )
            .then(async ({ status, response }) => {
                if (status.code === 'OK') {
                    const info = response;
                    if (info) {
                        await setAvas(info.partners);
                        await setAvas(info.referrals);
                    }
                    setOpenProfile(response);
                }
            });
    }

    const observer = useRef<IntersectionObserver>();
    const obserevItem = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (teamList.length >= statistics.partnersQuantity || teamList.length < 49) {
            observer.current?.disconnect();
            return;
        }

        if (observer.current) {
            observer.current.disconnect();
        }

        observer.current = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    loadPartners();
                }
            });
        });

        if (obserevItem?.current) {
            observer.current.observe(obserevItem.current);
        }

        return () => {
            if (observer.current) {
                observer.current.disconnect();
            }
        };
    }, [teamList]);

    return (
        <>
            <div
                className="team"
                onScroll={console.log}>
                <Title text={t('team.team_title')} />
                <Statistic />
                <Title text={t('team.partner_title')} />

                <div className="partners">
                    <div className="block">
                        <div
                            className="input"
                            onClick={() => setOpenSearch(true)}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none">
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M11.5 4C7.35786 4 4 7.35786 4 11.5C4 15.6421 7.35786 19 11.5 19C13.5131 19 15.341 18.2069 16.6881 16.916C16.7192 16.8729 16.7542 16.8317 16.793 16.7929C16.8318 16.7541 16.873 16.7191 16.9161 16.688C18.2069 15.3409 19 13.5131 19 11.5C19 7.35786 15.6421 4 11.5 4ZM18.8876 17.4733C20.2086 15.8414 21 13.7631 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21C13.7632 21 15.8415 20.2086 17.4734 18.8875L20.2929 21.7071C20.6834 22.0976 21.3166 22.0976 21.7071 21.7071C22.0976 21.3166 22.0976 20.6834 21.7071 20.2929L18.8876 17.4733Z"
                                    fill="#89B1FF"
                                />
                            </svg>
                            {t('team.search_title')}
                        </div>
                    </div>

                    {teamList.length > 0 && !searching && (
                        <>
                            {teamList.map((el, i) => {
                                if (teamList.length - 30 === i) {
                                    return (
                                        <>
                                            <div
                                                ref={obserevItem}
                                                className="observe-item"></div>
                                            <UserBanner
                                                key={el.account?.id + 'tm'}
                                                {...el}
                                                handleClick={() => el.account?.id && getAccount(el.account?.id)}
                                            />
                                        </>
                                    );
                                } else {
                                    return (
                                        <UserBanner
                                            key={el.account?.id + 'tm'}
                                            {...el}
                                            handleClick={() => el.account?.id && getAccount(el.account?.id)}
                                        />
                                    );
                                }
                            })}
                        </>
                    )}

                    {searching ||
                        (fetching && (
                            <div className="load">
                                <div className="spinner small"></div>
                            </div>
                        ))}
                </div>
            </div>

            {openSearch && (
                <IosModal
                    cancel={() => setOpenSearch(false)}
                    cancelBtnTitle={t('modals.ios.header.btns.cancel')}
                    title={t('modals.ios.header.titles.team')}>
                    <Search
                        team={true}
                        placeholder={t('modals.ios.header.titles.team_login')}
                        setSelect={getAccount}
                    />
                </IosModal>
            )}
            {openProfile && (
                <Modal
                    closeBtn={() => setOpenProfile(null)}
                    classModal="profile-modal">
                    <UserProfile {...openProfile} />
                </Modal>
            )}
        </>
    );
};

export default Team;
